/**
 * PDP Media Gallery (AC-146, 2026-07-13) — Figs overlay extras.
 * Loaded by snippets/pdp-media-gallery-content.liquid ONLY when the block
 * setting video_jump_button or favorites_heart is on; each feature activates
 * only when its (setting-gated) element exists, so galleries without the
 * settings are untouched.
 *
 * 1. "Play video" jump button (spec §5): clicking the top-right play circle
 *    opens the fullscreen zoom dialog positioned on the video slide (the
 *    dialog video autoplays there). The video slide index is re-derived from
 *    the dialog's media list at click time, so the variant-media in-place
 *    swap (PR #25 machinery) can never stale it. The button hides while the
 *    inline gallery's video slide is active (Figs shows it on image slides
 *    only — the video slide has its own bottom-right play/pause toggle).
 *
 * 2. Favorites heart (spec §3): VISUAL parity only — 42px white heart circle;
 *    toggled state persists per product id in localStorage (no backend).
 *
 * 2026-07-15 (SWA-40 session): reworked from per-node listeners to document
 * delegation. Variant changes morph-replace the overlay buttons via the
 * Section Rendering API, which silently dropped the old one-shot listeners
 * (heart/play dead after the first variant change). Clicks now delegate from
 * document and re-resolve their targets per event; a MutationObserver
 * re-syncs rendered state (heart aria-pressed, play-jump visibility) on any
 * morph that introduces new overlay buttons.
 */

const FAVORITES_KEY = 'pdp-gallery-favorites';

/** @returns {string[]} the stored favorited product ids (never throws). */
function readFavorites() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(FAVORITES_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

/** @param {string[]} ids - the favorited product ids to persist. */
function writeFavorites(ids) {
  try {
    window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable (private mode / quota) — the toggle still works visually for the session.
  }
}

/**
 * Finds the 0-based index of the first video item among the given elements.
 * @param {Iterable<Element>} items - slide/dialog media elements carrying product-media-container--* classes.
 * @returns {number} the index, or -1 when no video item exists.
 */
function findVideoIndex(items) {
  let index = 0;
  for (const item of items) {
    if (
      item.classList.contains('product-media-container--video') ||
      item.classList.contains('product-media-container--external_video')
    ) {
      return index;
    }
    index += 1;
  }
  return -1;
}

/**
 * @param {HTMLButtonElement} button - a [data-pdp-fav-heart] button.
 * @param {boolean} pressed - whether the product is favorited.
 */
function renderHeart(button, pressed) {
  button.setAttribute('aria-pressed', String(pressed));
  button.setAttribute('aria-label', pressed ? 'Remove from favorites' : 'Add to favorites');
}

/** Re-renders every heart on the page from localStorage (morphs reset the server-rendered aria-pressed to false). */
function syncHearts() {
  const favorites = readFavorites();
  for (const button of document.querySelectorAll('button[data-pdp-fav-heart]')) {
    renderHeart(/** @type {HTMLButtonElement} */ (button), favorites.includes(String(button.dataset.productId ?? '')));
  }
}

/** @param {HTMLElement} gallery - a pdp-media-gallery element containing a play-jump button. */
function syncPlayJumpVisibility(gallery) {
  const button = gallery.querySelector('button[data-pdp-play-jump]');
  if (!button) return;
  const slideshow = /** @type {any} */ (gallery.querySelector('pdp-slideshow-component[ref="slideshow"]'));
  const activeIndex = typeof slideshow?.current === 'number' ? slideshow.current : 0;
  const videoIndex = findVideoIndex(gallery.querySelectorAll('pdp-slideshow-component[ref="slideshow"] slideshow-slide'));
  /** @type {HTMLButtonElement} */ (button).hidden = videoIndex >= 0 && activeIndex === videoIndex;
}

// --- Delegated clicks: survive any Section Rendering morph, no per-node state. ---

document.addEventListener('click', (event) => {
  const target = /** @type {Element} */ (event.target);
  if (!(target instanceof Element)) return;

  const heart = target.closest('button[data-pdp-fav-heart]');
  if (heart) {
    const productId = String(/** @type {HTMLElement} */ (heart).dataset.productId ?? '');
    const favorites = readFavorites();
    const pressed = favorites.includes(productId);
    writeFavorites(pressed ? favorites.filter((id) => id !== productId) : [...favorites, productId]);
    renderHeart(/** @type {HTMLButtonElement} */ (heart), !pressed);
    return;
  }

  const play = target.closest('button[data-pdp-play-jump]');
  if (play) {
    const dialog = document.getElementById(/** @type {HTMLElement} */ (play).dataset.zoomDialogId ?? '');
    if (!dialog || typeof (/** @type {any} */ (dialog).open) !== 'function') return;
    const index = findVideoIndex(dialog.querySelectorAll('.dialog-zoomed-gallery > li'));
    if (index < 0) return;
    /** @type {any} */ (dialog).open(index, event);
  }
});

// Play-jump visibility tracks the active slide. slideshow:select is dispatched
// on the slideshow component; listen at document level (it bubbles through the
// gallery) and fall back to the morph observer below if a build stops it.
document.addEventListener('slideshow:select', (event) => {
  const gallery = /** @type {Element} */ (event.target)?.closest?.('pdp-media-gallery');
  if (gallery) syncPlayJumpVisibility(/** @type {HTMLElement} */ (gallery));
});

// --- Morph re-sync: new overlay buttons arrive with server-default state. ---

let syncQueued = false;
const queueSync = () => {
  if (syncQueued) return;
  syncQueued = true;
  // setTimeout, not requestAnimationFrame: rAF never fires while the tab is
  // hidden/not painting, which would leave morphed buttons stuck on their
  // server-default state until the next repaint.
  setTimeout(() => {
    syncQueued = false;
    syncHearts();
    for (const gallery of document.querySelectorAll('pdp-media-gallery')) {
      syncPlayJumpVisibility(/** @type {HTMLElement} */ (gallery));
    }
  }, 16);
};

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      // Cheap tagName/attribute pre-filter before any selector matching:
      // overlay buttons arrive either as the <button> itself or inside a
      // container; leaf nodes with no children can never contain one.
      const tag = node.tagName;
      if (tag === 'BUTTON') {
        if ('pdpFavHeart' in node.dataset || 'pdpPlayJump' in node.dataset) {
          queueSync();
          return;
        }
        continue;
      }
      if (tag === 'PDP-MEDIA-GALLERY') {
        queueSync();
        return;
      }
      if (node.childElementCount === 0) continue;
      if (node.querySelector('button[data-pdp-fav-heart], button[data-pdp-play-jump]')) {
        queueSync();
        return;
      }
    }
  }
});
// Overlay-button morphs only ever happen inside the product-information
// section; observing it (instead of the whole body) keeps unrelated DOM
// churn (drawers, cart, quick-add) out of the callback. The script can load
// on non-product pages, so fall back to body when the section is absent.
const observeRoot = document.querySelector('.product-information')?.closest('.shopify-section') ?? document.body;
observer.observe(observeRoot, { childList: true, subtree: true });

queueSync();
